import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  FormControlLabel,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Stack,
  TablePagination,
  TextField,
  Typography
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';

const statusColorMap = {
  composing: 'warning',
  scheduled: 'info',
  sending: 'info',
  sent: 'primary',
  failed: 'error'
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const formatScheduledSendAt = formatDateTime;

const getListDateText = (item) => {
  const created = formatDateTime(item.createdAt);
  if (!created) return '';

  if (item.status === 'sent' && item.sentAt) {
    return `Created ${created} · Sent ${formatDateTime(item.sentAt)}`;
  }
  if (item.status === 'scheduled' && item.scheduledSendAt) {
    return `Created ${created} · Sends ${formatDateTime(item.scheduledSendAt)}`;
  }
  return `Created ${created}`;
};

const formatTimeUntilSend = (scheduledSendAt, now = Date.now()) => {
  if (!scheduledSendAt) return '';
  const target = new Date(scheduledSendAt).getTime();
  if (Number.isNaN(target)) return '';

  const diffMs = target - now;
  if (diffMs <= 0) return 'Sending soon...';

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  }
  if (minutes > 0 || hours === 0) {
    parts.push(`${minutes} min${minutes === 1 ? '' : 's'}`);
  }

  return parts.join(' ');
};

const getDescriptionPreview = (text) => {
  const source = String(text || '').trim();
  if (!source) return '';
  const [firstLineRaw = ''] = source.split(/\r?\n/);
  const firstLine = firstLineRaw.trim();
  if (!firstLine) return '';
  return source === firstLine ? firstLine : `${firstLine} ...`;
};

const scheduledChipSx = (theme) => ({
  textTransform: 'capitalize',
  ...(theme.palette.mode === 'dark'
    ? {
        color: '#fff',
        '& .MuiChip-label': { color: '#fff' }
      }
    : {})
});

const ManualEmailApplications = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendOpen, setSendOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [sendSubmitting, setSendSubmitting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [detailSubmitting, setDetailSubmitting] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const [sendForm, setSendForm] = useState({
    resumeIds: [],
    toEmail: '',
    sourceUrl: '',
    description: '',
    customizeCoverLetter: false,
    customizeResume: false,
    sendAfterHours: 0,
    sendEveryHours: 2
  });

  const [detailForm, setDetailForm] = useState({
    id: '',
    status: '',
    description: '',
    sourceUrl: '',
    error: '',
    toEmail: '',
    subject: '',
    body: '',
    generatedResumePath: '',
    customizeCoverLetter: false,
    customizeResume: false,
    sendAfterHours: 0,
    scheduledSendAt: '',
    createdAt: '',
    updatedAt: '',
    sentAt: ''
  });

  const selectedResumes = useMemo(
    () => resumes.filter((resume) => sendForm.resumeIds.includes(resume._id)),
    [resumes, sendForm.resumeIds]
  );

  const selectedResumeName = useMemo(() => {
    if (!selectedResumes.length) return '';
    if (selectedResumes.length === 1) {
      return selectedResumes[0]?.content?.personal_info?.name || selectedResumes[0]?.nickname || '';
    }
    return `${selectedResumes.length} resumes`;
  }, [selectedResumes]);

  const isMultiResumeSend = sendForm.resumeIds.length > 1;

  const isDetailEditable = ['scheduled', 'failed', 'ready'].includes(detailForm.status);
  const canSendNow = ['scheduled', 'failed', 'ready'].includes(detailForm.status);

  const applyDetailItem = (item) => {
    setDetailForm({
      id: item._id,
      status: item.status || '',
      description: item.description || '',
      sourceUrl: item.sourceUrl || '',
      error: item.error || '',
      toEmail: item.detectedEmail || '',
      subject: item.subject || 'Application - Role',
      body: item.body || '',
      generatedResumePath: item.generatedResumePath || '',
      customizeCoverLetter: item.customizeCoverLetter === true,
      customizeResume: item.customizeResume === true,
      sendAfterHours: item.sendAfterHours ?? 0,
      scheduledSendAt: item.scheduledSendAt || '',
      createdAt: item.createdAt || '',
      updatedAt: item.updatedAt || '',
      sentAt: item.sentAt || ''
    });
  };

  const upsertItem = (updatedItem) => {
    if (!updatedItem?._id) return;
    setItems((prev) => {
      const without = prev.filter((row) => row._id !== updatedItem._id);
      return [updatedItem, ...without];
    });
    if (String(detailForm.id) === String(updatedItem._id)) {
      applyDetailItem(updatedItem);
    }
  };

  const fetchResumes = async () => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/resumes`);
    setResumes(Array.isArray(response.data) ? response.data : []);
  };

  const fetchItems = useCallback(async (pageIndex = page, pageSize = rowsPerPage) => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/manual-email-applications`, {
      params: {
        page: pageIndex + 1,
        limit: pageSize,
      },
    });
    setItems(Array.isArray(response.data?.items) ? response.data.items : []);
    setTotal(Number(response.data?.total) || 0);
    return response.data;
  }, [page, rowsPerPage]);

  const bootstrap = async () => {
    try {
      setLoading(true);
      await fetchResumes();
    } catch (error) {
      toast.error('Failed to load manual email applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    bootstrap();
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    fetchItems(page, rowsPerPage).catch(() => null);
  }, [page, rowsPerPage, fetchItems, loading]);

  useEffect(() => {
    if (loading) return undefined;
    const interval = setInterval(() => {
      fetchItems(page, rowsPerPage).catch(() => null);
    }, 4000);
    return () => clearInterval(interval);
  }, [fetchItems, page, rowsPerPage, loading]);

  useEffect(() => {
    const hasScheduled = items.some((item) => item.status === 'scheduled' && item.scheduledSendAt);
    if (!hasScheduled) return undefined;

    const interval = setInterval(() => {
      setNow(Date.now());
    }, 30000);

    return () => clearInterval(interval);
  }, [items]);

  useEffect(() => {
    if (!detailOpen) return;
    const id = detailForm.id;
    if (!id) return;
    const fresh = items.find((row) => String(row._id) === String(id));
    if (!fresh) return;
    setDetailForm((prev) => {
      if (String(prev.id) !== String(id)) return prev;
      if (prev.status === fresh.status) return prev;
      return {
        ...prev,
        status: fresh.status || '',
        description: fresh.description || '',
        error: fresh.error || '',
        toEmail: fresh.detectedEmail || prev.toEmail,
        subject: fresh.subject || prev.subject,
        body: fresh.body || prev.body,
        generatedResumePath: fresh.generatedResumePath || prev.generatedResumePath,
        customizeCoverLetter: fresh.customizeCoverLetter === true,
        customizeResume: fresh.customizeResume === true,
        sendAfterHours: fresh.sendAfterHours ?? prev.sendAfterHours,
        scheduledSendAt: fresh.scheduledSendAt || prev.scheduledSendAt,
        createdAt: fresh.createdAt || prev.createdAt,
        updatedAt: fresh.updatedAt || prev.updatedAt,
        sentAt: fresh.sentAt || prev.sentAt
      };
    });
  }, [items, detailOpen, detailForm.id]);

  const handleOpenSend = () => {
    setSendForm({
      resumeIds: resumes[0]?._id ? [resumes[0]._id] : [],
      toEmail: '',
      sourceUrl: '',
      description: '',
      customizeCoverLetter: false,
      customizeResume: false,
      sendAfterHours: 0,
      sendEveryHours: 2
    });
    setSendOpen(true);
  };

  const toggleResumeSelection = (resumeId) => {
    setSendForm((prev) => {
      const isSelected = prev.resumeIds.includes(resumeId);
      const resumeIds = isSelected
        ? prev.resumeIds.filter((id) => id !== resumeId)
        : [...prev.resumeIds, resumeId];
      return { ...prev, resumeIds };
    });
  };

  const handleSend = async () => {
    if (!sendForm.resumeIds.length) {
      toast.error('Please select at least one resume');
      return;
    }
    if (sendForm.customizeCoverLetter && !sendForm.description.trim()) {
      toast.error('Please enter a job description');
      return;
    }
    if (sendForm.customizeResume && !sendForm.description.trim()) {
      toast.error('Please enter a job description when customizing the resume');
      return;
    }
    if (!sendForm.toEmail.trim() && !sendForm.customizeCoverLetter) {
      toast.error('Please enter a recipient email');
      return;
    }
    if (!isMultiResumeSend) {
      if (!Number.isFinite(Number(sendForm.sendAfterHours)) || Number(sendForm.sendAfterHours) < 0) {
        toast.error('Please enter a valid number of hours (0 or more)');
        return;
      }
    } else if (!Number.isFinite(Number(sendForm.sendEveryHours)) || Number(sendForm.sendEveryHours) <= 0) {
      toast.error('Please enter a valid number of hours for the send interval');
      return;
    }

    try {
      setSendSubmitting(true);
      const payload = {
        toEmail: sendForm.toEmail.trim(),
        sourceUrl: sendForm.sourceUrl.trim(),
        description: sendForm.description,
        customizeCoverLetter: sendForm.customizeCoverLetter,
        customizeResume: sendForm.customizeResume
      };

      let response;
      if (isMultiResumeSend) {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/manual-email-applications/compose/batch`, {
          ...payload,
          resumeIds: sendForm.resumeIds,
          sendEveryHours: Number(sendForm.sendEveryHours)
        });
      } else {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/manual-email-applications/compose`, {
          ...payload,
          resumeId: sendForm.resumeIds[0],
          sendAfterHours: Number(sendForm.sendAfterHours)
        });
      }

      const createdItems = isMultiResumeSend
        ? (Array.isArray(response.data?.items) ? response.data.items : [])
        : (response.data?.item ? [response.data.item] : []);

      if (createdItems.length) {
        setPage(0);
        await fetchItems(0, rowsPerPage);
      } else {
        await fetchItems(page, rowsPerPage);
      }

      const batchErrors = Array.isArray(response.data?.errors) ? response.data.errors : [];
      setSendOpen(false);

      if (isMultiResumeSend) {
        const sentCount = createdItems.filter((item) => item.status === 'sent').length;
        const scheduledCount = createdItems.filter((item) => item.status === 'scheduled').length;
        const interval = response.data?.sendEveryHours ?? sendForm.sendEveryHours;
        if (batchErrors.length) {
          toast.warn(
            `Sent ${sentCount}, scheduled ${scheduledCount}. ${batchErrors.length} failed.`
          );
        } else {
          toast.success(
            `First email sent now. ${scheduledCount} more scheduled every ~${interval} hour(s) with random spacing.`
          );
        }
      } else {
        const sent = createdItems[0];
        if (sent?.status === 'scheduled') {
          toast.success(`Email scheduled to send in ${sent.sendAfterHours ?? sendForm.sendAfterHours} hour(s)`);
        } else {
          toast.success('Email sent');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send email');
      await fetchItems(page, rowsPerPage);
    } finally {
      setSendSubmitting(false);
    }
  };

  const handleOpenDetail = (item) => {
    applyDetailItem(item);
    setDetailOpen(true);
  };

  const handleSaveDetail = async () => {
    if (!detailForm.id || !isDetailEditable) return;

    try {
      setDetailSubmitting(true);
      const payload = {
        subject: detailForm.subject,
        body: detailForm.body
      };
      if (detailForm.status === 'scheduled') {
        const hours = Number(detailForm.sendAfterHours);
        if (Number.isFinite(hours) && hours > 0) {
          payload.sendAfterHours = hours;
        }
      }

      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/manual-email-applications/${detailForm.id}`,
        payload
      );
      upsertItem(response.data?.item);
      toast.success('Application updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update application');
    } finally {
      setDetailSubmitting(false);
    }
  };

  const handleSendNow = async () => {
    if (!detailForm.id || !canSendNow) return;

    try {
      setDetailSubmitting(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/manual-email-applications/${detailForm.id}/send`,
        {
          subject: detailForm.subject,
          body: detailForm.body
        }
      );
      upsertItem(response.data?.item);
      toast.success('Email sent');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send email');
      await fetchItems(page, rowsPerPage);
    } finally {
      setDetailSubmitting(false);
    }
  };

  const handleDelete = async (item, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!window.confirm('Delete this manual email application?')) {
      return;
    }
    try {
      setDeleteSubmitting(true);
      await axios.delete(`${process.env.REACT_APP_API_URL}/manual-email-applications/${item._id}`);
      if (detailForm.id === item._id) {
        setDetailOpen(false);
      }
      if (items.length === 1 && page > 0) {
        setPage((prev) => prev - 1);
      } else {
        await fetchItems(page, rowsPerPage);
      }
      toast.success('Deleted');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5">Manual Email Applications</Typography>
            <Typography variant="body2" color="text.secondary">
              Send email applications with optional AI-tailored cover letters and resumes.
            </Typography>
          </Box>
          <Button variant="contained" onClick={handleOpenSend}>
            Send Application
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {!items.length ? (
          <Typography color="text.secondary">No manual email applications yet.</Typography>
        ) : (
          <List>
            {items.map((item) => (
              <ListItem
                key={item._id}
                disablePadding
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="Delete"
                    disabled={deleteSubmitting}
                    onClick={(e) => handleDelete(item, e)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                }
              >
                <ListItemButton onClick={() => handleOpenDetail(item)} sx={{ pr: 10 }}>
                  <ListItemText
                    primary={item.subject || item.detectedEmail || 'Email application'}
                    secondary={
                      <>
                        {(getDescriptionPreview(item.description) || item.detectedEmail) && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            display="block"
                          >
                            {getDescriptionPreview(item.description) || item.detectedEmail}
                          </Typography>
                        )}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          {getListDateText(item)}
                        </Typography>
                      </>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                  {item.status === 'scheduled' ? (
                    <Stack alignItems="center" spacing={0.25} sx={{ ml: 1, flexShrink: 0 }}>
                      <Chip
                        size="small"
                        label={item.status}
                        color={statusColorMap[item.status] || 'default'}
                        sx={scheduledChipSx}
                      />
                      {item.scheduledSendAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {formatTimeUntilSend(item.scheduledSendAt, now)}
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Chip
                      size="small"
                      label={item.status}
                      color={statusColorMap[item.status] || 'default'}
                      sx={{ textTransform: 'capitalize', ml: 1, flexShrink: 0 }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        )}
      </Paper>

      <Dialog open={sendOpen} onClose={() => !sendSubmitting && setSendOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Send Email Application</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Resumes
            </Typography>
            <Stack spacing={0.5}>
              {resumes.map((resume) => {
                const displayName = resume.content?.personal_info?.name || resume.nickname || 'Unnamed Resume';
                return (
                  <FormControlLabel
                    key={resume._id}
                    control={
                      <Checkbox
                        checked={sendForm.resumeIds.includes(resume._id)}
                        disabled={sendSubmitting}
                        onChange={() => toggleResumeSelection(resume._id)}
                      />
                    }
                    label={displayName}
                  />
                );
              })}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Select one or more resumes. Settings below apply to all selected resumes.
            </Typography>
          </Box>
          <TextField
            label="Email address"
            type="email"
            fullWidth
            margin="normal"
            value={sendForm.toEmail}
            onChange={(e) => setSendForm((prev) => ({ ...prev, toEmail: e.target.value }))}
            placeholder="recruiter@company.com"
            helperText={
              sendForm.customizeCoverLetter
                ? 'Optional if the job description includes an application email.'
                : 'Required when not using a customized cover letter.'
            }
            disabled={sendSubmitting}
          />
          <FormControlLabel
            sx={{ mt: 1, display: 'block' }}
            control={
              <Checkbox
                checked={sendForm.customizeCoverLetter}
                disabled={sendSubmitting}
                onChange={(e) =>
                  setSendForm((prev) => ({
                    ...prev,
                    customizeCoverLetter: e.target.checked,
                    description: e.target.checked ? prev.description : ''
                  }))
                }
              />
            }
            label="Customize cover letter for this job"
          />
          {(sendForm.customizeCoverLetter || sendForm.customizeResume) && (
            <TextField
              label="Job description (can include application email)"
              multiline
              minRows={6}
              fullWidth
              margin="normal"
              value={sendForm.description}
              onChange={(e) => setSendForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder={`Paste the job description for ${selectedResumeName || 'selected resume'}`}
              disabled={sendSubmitting}
            />
          )}
          {(sendForm.customizeCoverLetter || sendForm.customizeResume) && (
            <TextField
              label="Job posting URL"
              fullWidth
              margin="normal"
              value={sendForm.sourceUrl}
              onChange={(e) => setSendForm((prev) => ({ ...prev, sourceUrl: e.target.value }))}
              placeholder="https://company.com/jobs/role-id"
              helperText={
                sendForm.customizeResume
                  ? 'Required when customizing the resume. You can also include the URL in the job description.'
                  : 'Optional. Used to link this application to the job posting.'
              }
              disabled={sendSubmitting}
            />
          )}
          <FormControlLabel
            sx={{ mt: 1, display: 'block' }}
            control={
              <Checkbox
                checked={sendForm.customizeResume}
                disabled={sendSubmitting}
                onChange={(e) =>
                  setSendForm((prev) => ({ ...prev, customizeResume: e.target.checked }))
                }
              />
            }
            label="Customize resume for this job"
          />
          <Typography variant="body2" color="text.secondary">
            {sendForm.customizeCoverLetter
              ? 'A tailored cover letter will be generated from the job description.'
              : 'The saved cover letter from your resume profile will be used.'}
            {sendForm.customizeResume
              ? ' A customized resume PDF will be generated.'
              : ' Your base resume PDF will be attached as-is.'}
          </Typography>
          {isMultiResumeSend ? (
            <TextField
              label="Send every (hours)"
              type="number"
              fullWidth
              margin="normal"
              inputProps={{ min: 1, max: 168, step: 1 }}
              value={sendForm.sendEveryHours}
              onChange={(e) => setSendForm((prev) => ({ ...prev, sendEveryHours: e.target.value }))}
              helperText="The first resume sends immediately. Each additional resume is scheduled with this interval plus 0-30 random minutes."
              disabled={sendSubmitting}
            />
          ) : (
            <TextField
              label="Send after (hours)"
              type="number"
              fullWidth
              margin="normal"
              inputProps={{ min: 0, max: 168, step: 1 }}
              value={sendForm.sendAfterHours}
              onChange={(e) => setSendForm((prev) => ({ ...prev, sendAfterHours: e.target.value }))}
              helperText="0 sends immediately. Set hours to schedule a delayed send."
              disabled={sendSubmitting}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendOpen(false)} disabled={sendSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSend} disabled={sendSubmitting}>
            {sendSubmitting
              ? (isMultiResumeSend ? 'Processing...' : (Number(sendForm.sendAfterHours) > 0 ? 'Scheduling...' : 'Sending...'))
              : (isMultiResumeSend
                ? 'Send Applications'
                : (Number(sendForm.sendAfterHours) > 0 ? 'Schedule Send' : 'Send Now'))}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => !detailSubmitting && setDetailOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Application Details</DialogTitle>
        <DialogContent>
          {(detailForm.status === 'composing' || detailForm.status === 'sending') && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {detailForm.status === 'composing' ? 'Composing application...' : 'Sending email...'}
            </Alert>
          )}
          {detailForm.status === 'scheduled' && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Scheduled to send in {formatTimeUntilSend(detailForm.scheduledSendAt, now)}
              {detailForm.scheduledSendAt ? ` (${formatScheduledSendAt(detailForm.scheduledSendAt)})` : ''}.
            </Alert>
          )}
          {detailForm.status === 'failed' && detailForm.error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {detailForm.error}
            </Alert>
          )}
          {detailForm.status === 'sent' && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Email sent successfully
              {detailForm.sentAt ? ` on ${formatDateTime(detailForm.sentAt)}` : '.'}
            </Alert>
          )}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Created:</strong> {formatDateTime(detailForm.createdAt) || '—'}
            </Typography>
            {detailForm.updatedAt && detailForm.updatedAt !== detailForm.createdAt && (
              <Typography variant="body2" color="text.secondary">
                <strong>Updated:</strong> {formatDateTime(detailForm.updatedAt)}
              </Typography>
            )}
            {detailForm.scheduledSendAt && (
              <Typography variant="body2" color="text.secondary">
                <strong>Scheduled send:</strong> {formatDateTime(detailForm.scheduledSendAt)}
              </Typography>
            )}
            {detailForm.sentAt && (
              <Typography variant="body2" color="text.secondary">
                <strong>Sent:</strong> {formatDateTime(detailForm.sentAt)}
              </Typography>
            )}
          </Box>
          {(detailForm.customizeCoverLetter || detailForm.customizeResume) && (
            <TextField
              label="Original job description"
              fullWidth
              margin="normal"
              multiline
              minRows={4}
              value={detailForm.description}
              InputProps={{ readOnly: true }}
            />
          )}
          {detailForm.sourceUrl && (
            <TextField
              label="Job posting URL"
              fullWidth
              margin="normal"
              value={detailForm.sourceUrl}
              InputProps={{ readOnly: true }}
            />
          )}
          <TextField
            label="To"
            fullWidth
            margin="normal"
            value={detailForm.toEmail}
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="Subject"
            fullWidth
            margin="normal"
            value={detailForm.subject}
            onChange={(e) => setDetailForm((prev) => ({ ...prev, subject: e.target.value }))}
            InputProps={{ readOnly: !isDetailEditable }}
            disabled={detailSubmitting}
          />
          <TextField
            label="Content"
            fullWidth
            margin="normal"
            multiline
            minRows={8}
            value={detailForm.body}
            onChange={(e) => setDetailForm((prev) => ({ ...prev, body: e.target.value }))}
            InputProps={{ readOnly: !isDetailEditable }}
            disabled={detailSubmitting}
          />
          {detailForm.status === 'scheduled' && Number(detailForm.sendAfterHours) > 0 && (
            <TextField
              label="Send after (hours)"
              type="number"
              fullWidth
              margin="normal"
              inputProps={{ min: 1, max: 168, step: 1 }}
              value={detailForm.sendAfterHours}
              onChange={(e) => setDetailForm((prev) => ({ ...prev, sendAfterHours: e.target.value }))}
              helperText="Updates the delay from now. Use Send Now to send immediately."
              disabled={detailSubmitting}
            />
          )}
          {detailForm.generatedResumePath && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  window.open(`${process.env.REACT_APP_API_URL}/resumefiles/${detailForm.generatedResumePath}`, '_blank');
                }}
              >
                {detailForm.customizeResume ? 'View Customized Resume' : 'View Resume'}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={() => handleDelete({ _id: detailForm.id })}
            disabled={deleteSubmitting || detailSubmitting || !detailForm.id}
          >
            Delete
          </Button>
          <Box sx={{ flex: 1 }} />
          {canSendNow && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendNow}
              disabled={detailSubmitting}
            >
              {detailSubmitting ? 'Sending...' : 'Send Now'}
            </Button>
          )}
          {isDetailEditable && (
            <Button
              variant="outlined"
              onClick={handleSaveDetail}
              disabled={detailSubmitting}
            >
              {detailSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)} disabled={detailSubmitting}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualEmailApplications;
